import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Plane, Store } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { errorMessage, startCheckout } from "@/lib/api";
import { BusinessFields } from "@/components/BusinessFields";
import { PlanChoice } from "@/components/PlanChoice";
import { DeviceLimitPrompt } from "@/components/DeviceLimitPrompt";

export default function AuthPage({ mode }) {
  const isLogin = mode === "login";
  const [params] = useSearchParams();
  const [role, setRole] = useState(params.get("role") === "vendor" ? "vendor" : "user");
  const [plan, setPlan] = useState(params.get("plan") === "family" ? "family" : "user");
  const [limit, setLimit] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", password: "", promo_code: "", business_name: "", business_type: "", business_description: "" });
  const [showPromo, setShowPromo] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const submit = async (e, signout_sid = "") => {
    e?.preventDefault();
    setError(""); setBusy(true);
    if (!isLogin && role === "vendor" && !form.business_type) { setError("Please pick your business type."); setBusy(false); return; }
    try {
      const u = isLogin ? await login({ email: form.email, password: form.password, signout_sid }) : await register({ ...form, role, plan: role === "user" ? plan : "user" });
      if (!isLogin && u.needs_card) { await startCheckout(u.checkout_plan); return; }
      const pending = localStorage.getItem("vv_pending_query");
      localStorage.removeItem("vv_pending_query");
      if (u.role === "vendor") navigate("/vendor");
      else navigate(pending ? `/explore?${pending}` : "/explore");
    } catch (err) {
      const d = err?.response?.data?.detail;
      if (d?.code === "device_limit") { setLimit(d); setError(""); } else setError(errorMessage(err));
    }
    finally { setBusy(false); }
  };

  const field = (k, label, type = "text", ph = "") => (
    <div className="space-y-2">
      <Label htmlFor={k} className="text-slate-300">{label}</Label>
      <Input id={k} data-testid={`auth-${k}-input`} type={type} required={k !== "promo_code"} placeholder={ph} value={form[k]}
        onChange={(e) => setForm({ ...form, [k]: e.target.value })}
        className="h-12 rounded-xl bg-[#0B0F19] border-[#26334D] focus-visible:ring-[#FF3B6B]" />
    </div>
  );

  return (
    <main className="max-w-md mx-auto px-4 py-16">
      <div className="rise rounded-3xl bg-[#151C2C] border border-[#26334D] p-8">
        <h1 className="text-3xl font-extrabold tracking-tight">{isLogin ? "Welcome back" : "Start exploring"}</h1>
        <p className="text-slate-400 mt-2 text-sm">{isLogin ? "Log in to pick up where you left off." : role === "user" ? (plan === "family" ? "Your first 10 days are completely free." : "Your first 7 days are completely free.") : "List your business for $25/month."}</p>
        {!isLogin && (
          <div className="grid grid-cols-2 gap-2 mt-6 p-1 rounded-full bg-[#0B0F19] border border-[#26334D]">
            {[["user", "Traveler", Plane], ["vendor", "Business", Store]].map(([r, l, Icon]) => (
              <button key={r} type="button" data-testid={`role-${r}-toggle`} onClick={() => setRole(r)}
                className={`flex items-center justify-center gap-2 h-10 rounded-full text-sm font-semibold transition-colors ${role === r ? "bg-[#FF3B6B] text-white" : "text-slate-400 hover:text-white"}`}>
                <Icon className="h-4 w-4" /> {l}
              </button>
            ))}
          </div>
        )}
        <form onSubmit={submit} className="mt-6 space-y-4">
          {!isLogin && role === "user" && <PlanChoice plan={plan} setPlan={setPlan} />}
          {!isLogin && field("name", role === "vendor" ? "Your name" : "Name", "text", "Alex Rivera")}
          {!isLogin && role === "vendor" && <BusinessFields form={form} setForm={setForm} field={field} />}
          {field("email", "Email", "email", "you@example.com")}
          {field("password", "Password", "password", "At least 6 characters")}
          {isLogin && (
            <div className="-mt-1 text-right">
              <Link data-testid="auth-forgot-password-link" to="/forgot-password" className="text-sm text-[#00E5A3] hover:underline">Forgot password?</Link>
            </div>
          )}
          {!isLogin && (showPromo ? field("promo_code", "Promo code (optional)", "text", "ENTER CODE") : (
            <button type="button" data-testid="auth-show-promo" onClick={() => setShowPromo(true)} className="text-sm text-[#00E5A3] hover:underline">Have a promo code?</button>
          ))}
          {error && <p data-testid="auth-error" className="text-sm text-[#FF7A7A]">{error}</p>}
          {isLogin && limit && <DeviceLimitPrompt info={limit} busy={busy} onSignOut={(sid) => submit(null, sid)} />}
          <Button data-testid="auth-submit-button" disabled={busy} type="submit" className="w-full h-12 rounded-full bg-[#FF3B6B] hover:bg-[#E02E5A] text-white font-semibold">
            {busy ? "One moment…" : isLogin ? "Log in" : role === "vendor" ? "Create business account" : plan === "family" ? "Start our free 10 days" : "Start my free week"}
          </Button>
          {!isLogin && !form.promo_code.trim() && (
            <p data-testid="auth-card-note" className="text-xs text-slate-400 text-center">
              Next, add a card on Stripe's secure page. You won't be charged until your free trial ends, then it renews monthly. Cancel anytime.
            </p>
          )}
        </form>
        <p className="text-sm text-slate-400 mt-6 text-center">
          {isLogin ? "New here? " : "Already have an account? "}
          <Link data-testid="auth-switch-link" to={isLogin ? "/register" : "/login"} className="text-[#FF3B6B] font-semibold">{isLogin ? "Create an account" : "Log in"}</Link>
        </p>
      </div>
    </main>
  );
}
